import { useGetFeaturedContentIdsQuery } from "../api/featuredApi";
import { useGetProductsByIdsQuery } from "../../product/api/productApi";
import {
  useGetCategoriesQuery,
  useGetTagsQuery,
} from "../../product/api/productTaxonomyApi";

/**
 * Orchestrates fetching featured content.
 * 1. Gets IDs from the custom endpoint.
 * 2. Fetches full Product data for those IDs.
 * 3. Filters Category/Tag data (which is usually pre-fetched/cached) by those IDs.
 */
export const useFeaturedContent = () => {
  // 1. Fetch IDs
  const {
    data: idsData,
    isLoading: isIdsLoading,
    isError: isIdsError,
  } = useGetFeaturedContentIdsQuery();

  const productIds = idsData?.productIds || [];
  const categoryIds = idsData?.categoryIds || [];
  const tagIds = idsData?.tagIds || [];

  // 2. Fetch Products (Skip if no IDs or while loading IDs)
  const shouldFetchProducts = productIds.length > 0;
  const {
    data: products,
    isLoading: isProductsLoading,
    isError: isProductsError,
  } = useGetProductsByIdsQuery(productIds, {
    skip: !shouldFetchProducts,
  });

  // 3. Fetch Categories (Typically cached)
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetCategoriesQuery();

  // 4. Fetch Tags (Typically cached)
  const {
    data: tagsData,
    isLoading: isTagsLoading,
    isError: isTagsError,
  } = useGetTagsQuery();

  // --- Filter Categories/Tags locally ---
  // We don't have a `getByIds` for taxonomies because we fetch ALL of them once and cache them.
  const featuredCategories =
    categoriesData && categoryIds.length > 0
      ? categoryIds
          .map((id) => categoriesData.categories[id])
          .filter((cat) => cat !== undefined)
      : [];

  const featuredTags =
    tagsData && tagIds.length > 0
      ? tagIds.map((id) => tagsData.tags[id]).filter((tag) => tag !== undefined)
      : [];

  // Combined Loading State
  const isLoading =
    isIdsLoading ||
    (shouldFetchProducts && isProductsLoading) ||
    isCategoriesLoading ||
    isTagsLoading;

  // Combined Error State
  const isError =
    isIdsError || isProductsError || isCategoriesError || isTagsError;

  return {
    products: products || [],
    categories: featuredCategories,
    tags: featuredTags,
    isLoading,
    isError,
  };
};
